import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type { ProspectStage, ProspectStatus, ProspectSummary } from '@bits/shared/zod';
import { formatInr, paise, rupeesToPaise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api, ApiRequestError } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Field } from '@/components/ui/field';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export const salesProspectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sales/prospects',
  component: SalesProspectsPage,
});

const STAGES: readonly ProspectStage[] = [
  'lead',
  'contacted',
  'proposal_sent',
  'negotiation',
  'won',
  'lost',
];

const STATUSES: readonly ProspectStatus[] = ['active', 'dormant', 'archived'];

const STAGE_LABEL: Record<ProspectStage, string> = {
  lead: 'Lead',
  contacted: 'Contacted',
  proposal_sent: 'Proposal sent',
  negotiation: 'Negotiation',
  won: 'Won',
  lost: 'Lost',
};

type ProspectSortKey = 'prospect' | 'stage' | 'owner' | 'channel' | 'nextTouch' | 'value' | 'flags';
const EMPTY_PROSPECTS: ProspectSummary[] = [];

const prospectSortAccessors: SortAccessors<ProspectSummary, ProspectSortKey> = {
  prospect: (p) => p.displayName,
  stage: (p) => STAGES.indexOf(p.stage),
  owner: (p) => p.ownerName,
  channel: (p) => p.leadChannelName,
  nextTouch: (p) => p.nextTouchDate,
  value: (p) => p.estimatedValuePaise,
  flags: (p) =>
    Number(p.isStale) + Number(p.status !== 'active') + Number(p.convertedClientId !== null),
};

export function stageBadgeVariant(stage: ProspectStage): BadgeProps['variant'] {
  switch (stage) {
    case 'lead':
      return 'muted';
    case 'contacted':
      return 'secondary';
    case 'proposal_sent':
      return 'outline';
    case 'negotiation':
      return 'default';
    case 'won':
      return 'default';
    case 'lost':
      return 'destructive';
  }
}

export function stageLabel(stage: ProspectStage): string {
  return STAGE_LABEL[stage];
}

function todayIso(): string {
  const d = new Date();
  const off = (5 * 60 + 30) * 60 * 1000;
  return new Date(d.getTime() + off).toISOString().slice(0, 10);
}

type CreateFormValues = {
  displayName: string;
  primaryContactName: string;
  primaryContactEmail: string;
  primaryContactPhone: string;
  leadChannelId: string;
  ownerId: string;
  serviceOfferingIds: string[];
  estimatedValueRupees: string;
  expectedCloseDate: string;
  nextTouchDate: string;
  currentClientId: string;
  notes: string;
};

function SalesProspectsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [ownerFilter, setOwnerFilter] = React.useState('');
  const [stageFilter, setStageFilter] = React.useState<ProspectStage | ''>('');
  const [statusFilter, setStatusFilter] = React.useState<ProspectStatus | ''>('active');
  const [channelFilter, setChannelFilter] = React.useState('');
  const [serviceFilter, setServiceFilter] = React.useState('');
  const [staleOnly, setStaleOnly] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [page, setPage] = React.useState(1);
  const pageSize = 25;
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: [
      'sales-prospects',
      { ownerFilter, stageFilter, statusFilter, channelFilter, serviceFilter, staleOnly, search, page },
    ],
    queryFn: () =>
      api.listProspects({
        ownerId: ownerFilter || undefined,
        stage: stageFilter || undefined,
        status: statusFilter || undefined,
        leadChannelId: channelFilter || undefined,
        serviceOfferingId: serviceFilter || undefined,
        staleOnly: staleOnly || undefined,
        search: search || undefined,
        page,
        pageSize,
      }),
  });

  const peopleQuery = useQuery({ queryKey: ['people-simple'], queryFn: () => api.listPeopleSimple() });
  const channelsQuery = useQuery({ queryKey: ['channels'], queryFn: () => api.listLeadChannels() });
  const servicesQuery = useQuery({
    queryKey: ['sales-services'],
    queryFn: () => api.listServiceOfferings(),
  });
  const clientsQuery = useQuery({
    queryKey: ['clients-for-prospects'],
    queryFn: () => api.listClients({ status: 'active', page: 1, pageSize: 100 }),
  });

  const form = useForm<CreateFormValues>({
    defaultValues: {
      displayName: '',
      primaryContactName: '',
      primaryContactEmail: '',
      primaryContactPhone: '',
      leadChannelId: '',
      ownerId: '',
      serviceOfferingIds: [],
      estimatedValueRupees: '',
      expectedCloseDate: '',
      nextTouchDate: '',
      currentClientId: '',
      notes: '',
    },
  });

  const createMutation = useMutation({
    mutationFn: (v: CreateFormValues) => {
      const estRupees = v.estimatedValueRupees.trim();
      const estPaise =
        estRupees === '' ? undefined : Number(rupeesToPaise(Number(estRupees)));
      return api.createProspect({
        displayName: v.displayName.trim(),
        primaryContactName: v.primaryContactName.trim() || undefined,
        primaryContactEmail: v.primaryContactEmail.trim() || undefined,
        primaryContactPhone: v.primaryContactPhone.trim() || undefined,
        leadChannelId: v.leadChannelId,
        ownerId: v.ownerId,
        serviceOfferingIds: v.serviceOfferingIds,
        estimatedValuePaise: estPaise,
        expectedCloseDate: v.expectedCloseDate || undefined,
        nextTouchDate: v.nextTouchDate || undefined,
        currentClientId: v.currentClientId || undefined,
        notes: v.notes.trim() || undefined,
      });
    },
    onSuccess: async () => {
      setDialogOpen(false);
      form.reset();
      setErrorMessage(null);
      await queryClient.invalidateQueries({ queryKey: ['sales-prospects'] });
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const { rows: items, ...sorter } = useSortedRows(
    listQuery.data?.items ?? EMPTY_PROSPECTS,
    prospectSortAccessors,
  );
  const total = listQuery.data?.total ?? 0;
  const people = peopleQuery.data ?? [];
  const channels = channelsQuery.data ?? [];
  const services = servicesQuery.data ?? [];
  const activeServices = services.filter((s) => s.active);
  const clients = clientsQuery.data?.items ?? [];

  const today = todayIso();

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">Prospects</h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Sales pipeline before a client exists. Convert on Won.
          </p>
        </div>
        <Dialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setErrorMessage(null);
              form.reset();
            }
          }}
        >
          <DialogTrigger asChild>
            <Button>New prospect</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>New prospect</DialogTitle>
              <DialogDescription>
                Prospects start in the <em>lead</em> stage. Value is captured in rupees, stored as paise.
              </DialogDescription>
            </DialogHeader>
            <form
              onSubmit={form.handleSubmit((v) => createMutation.mutate(v))}
              className="flex flex-col gap-3"
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Display name" required error={form.formState.errors.displayName?.message}>
                  <Input {...form.register('displayName', { required: 'Enter a name' })} />
                </Field>
                <Field label="Owner" required error={form.formState.errors.ownerId?.message}>
                  <Select {...form.register('ownerId', { required: 'Pick an owner' })}>
                    <option value="">Select…</option>
                    {people.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Lead channel" required error={form.formState.errors.leadChannelId?.message}>
                  <Select {...form.register('leadChannelId', { required: 'Pick a lead channel' })}>
                    <option value="">Select…</option>
                    {channels.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Existing client (for renewal)">
                  <Select {...form.register('currentClientId')} disabled={clientsQuery.isError}>
                    <option value="">
                      {clientsQuery.isError ? 'Could not load clients' : '— None —'}
                    </option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.displayName}
                      </option>
                    ))}
                  </Select>
                  {clientsQuery.isError ? (
                    <p className="text-xs text-[hsl(var(--destructive))]">
                      {clientsQuery.error instanceof ApiRequestError
                        ? clientsQuery.error.message
                        : 'Client list unavailable.'}
                    </p>
                  ) : null}
                </Field>
                <Field label="Contact name">
                  <Input {...form.register('primaryContactName')} />
                </Field>
                <Field label="Contact email">
                  <Input type="email" {...form.register('primaryContactEmail')} />
                </Field>
                <Field label="Contact phone">
                  <Input {...form.register('primaryContactPhone')} />
                </Field>
                <Field label="Estimated value (₹)">
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    {...form.register('estimatedValueRupees')}
                  />
                </Field>
                <Field label="Expected close date">
                  <Input type="date" {...form.register('expectedCloseDate')} />
                </Field>
                <Field label="Next touch date">
                  <Input type="date" {...form.register('nextTouchDate')} />
                </Field>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label>Service interests</Label>
                <div className="flex flex-wrap gap-2 rounded-xl border border-[hsl(var(--input))] px-3 py-2 text-sm">
                  {activeServices.length === 0 ? (
                    <span className="text-[hsl(var(--muted-foreground))]">
                      No active services — configure in Sales → Services.
                    </span>
                  ) : (
                    activeServices.map((s) => (
                      <label key={s.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          value={s.id}
                          {...form.register('serviceOfferingIds')}
                        />
                        <span>{s.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <Field label="Notes">
                <Textarea rows={3} {...form.register('notes')} />
              </Field>

              {errorMessage ? (
                <Alert variant="destructive">
                  <AlertTitle>Create failed</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              ) : null}

              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Saving…' : 'Create prospect'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Narrow the pipeline.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <Label>Owner</Label>
              <Select value={ownerFilter} onChange={(e) => { setOwnerFilter(e.currentTarget.value); setPage(1); }}>
                <option value="">All</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Stage</Label>
              <Select
                value={stageFilter}
                onChange={(e) => { setStageFilter(e.currentTarget.value as ProspectStage | ''); setPage(1); }}
              >
                <option value="">All</option>
                {STAGES.map((s) => (
                  <option key={s} value={s}>
                    {STAGE_LABEL[s]}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Status</Label>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.currentTarget.value as ProspectStatus | ''); setPage(1); }}
              >
                <option value="">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Channel</Label>
              <Select value={channelFilter} onChange={(e) => { setChannelFilter(e.currentTarget.value); setPage(1); }}>
                <option value="">All</option>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Service</Label>
              <Select value={serviceFilter} onChange={(e) => { setServiceFilter(e.currentTarget.value); setPage(1); }}>
                <option value="">All</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-1 flex-col gap-1.5 md:col-span-2">
              <Label>Search</Label>
              <Input
                placeholder="Search name or contact…"
                value={search}
                onChange={(e) => { setSearch(e.currentTarget.value); setPage(1); }}
              />
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={staleOnly}
                  onChange={(e) => { setStaleOnly(e.currentTarget.checked); setPage(1); }}
                />
                Stale only
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Prospects</CardTitle>
          <CardDescription>{total} total · page {page}</CardDescription>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">No prospects match.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  <tr className="border-b border-[hsl(var(--border))]">
                    <SortableTh sortKey="prospect" controller={sorter} className="py-2 pr-3">
                      Prospect
                    </SortableTh>
                    <SortableTh sortKey="stage" controller={sorter} className="py-2 pr-3">
                      Stage
                    </SortableTh>
                    <SortableTh sortKey="owner" controller={sorter} className="py-2 pr-3">
                      Owner
                    </SortableTh>
                    <SortableTh sortKey="channel" controller={sorter} className="py-2 pr-3">
                      Channel
                    </SortableTh>
                    <SortableTh sortKey="nextTouch" controller={sorter} className="py-2 pr-3">
                      Next touch
                    </SortableTh>
                    <SortableTh sortKey="value" controller={sorter} align="right" className="py-2 pr-3">
                      Est. value
                    </SortableTh>
                    <SortableTh sortKey="flags" controller={sorter} className="py-2 pr-3">
                      Flags
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {items.map((p) => {
                    const nextTouchPast =
                      p.nextTouchDate !== null && p.nextTouchDate < today;
                    return (
                      <tr key={p.id} className="border-b border-[hsl(var(--border))]/60 hover:bg-[hsl(var(--accent))]/40">
                        <td className="py-2 pr-3 font-medium">
                          <Link to="/sales/prospects/$prospectId" params={{ prospectId: p.id }}>
                            {p.displayName}
                          </Link>
                          {p.primaryContactName ? (
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">
                              {p.primaryContactName}
                            </div>
                          ) : null}
                        </td>
                        <td className="py-2 pr-3">
                          <Badge variant={stageBadgeVariant(p.stage)}>{STAGE_LABEL[p.stage]}</Badge>
                        </td>
                        <td className="py-2 pr-3">{p.ownerName}</td>
                        <td className="py-2 pr-3 text-[hsl(var(--muted-foreground))]">
                          {p.leadChannelName}
                        </td>
                        <td className={`py-2 pr-3 ${nextTouchPast ? 'text-[hsl(var(--destructive))]' : ''}`}>
                          {p.nextTouchDate ?? '—'}
                        </td>
                        <td className="py-2 pr-3 text-right tabular-nums">
                          {p.estimatedValuePaise !== null
                            ? formatInr(paise(BigInt(p.estimatedValuePaise)))
                            : '—'}
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex flex-wrap gap-1">
                            {p.isStale ? <Badge variant="destructive">stale</Badge> : null}
                            {p.status !== 'active' ? <Badge variant="muted">{p.status}</Badge> : null}
                            {p.convertedClientId ? <Badge variant="default">converted</Badge> : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <span className="text-xs text-[hsl(var(--muted-foreground))]">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={items.length < pageSize}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </CardContent>
      </Card>
    </AppShell>
  );
}

